import isEqual from 'lodash/isEqual'

import type { Contact } from '@features/contacts/types/contact'
import type { ContactPerson } from '@features/contacts/types/contactPerson'
import { ContactType } from '@features/contacts/types/contactType'
import { joinPersonName } from '@features/contacts/utils/joinPersonName'

import type { SupplierFormValues } from '../types/supplierForm'
import { getAdditionalContactPersonsPayload, getPrimaryContactPersonPayload } from './getSupplierContactPersonsPayload'
import { getSupplierContactType, getSupplierFormDefaultValues } from './supplierForm'

const TEXT_FIELDS = [
  'cityText',
  'countryId',
  'defaultExpenseProductDescription',
  'phone',
  'registrationNo',
  'stateText',
  'street',
  'vatNo',
  'zipcodeText',
] as const

type ClearableIdField = 'defaultExpenseAccountId' | 'defaultTaxRateId'

const getTextFieldsPayload = (values: SupplierFormValues, savedValues: SupplierFormValues): Partial<Contact> => {
  const payload: Partial<Contact> = {}

  for (const field of TEXT_FIELDS) {
    if (values[field] !== savedValues[field]) {
      payload[field] = values[field]
    }
  }

  return payload
}

// A cleared account or tax rate must be sent as `null`: an empty value is dropped from the request and keeps the old one.
const getClearableIdFieldPayload = (
  field: ClearableIdField,
  values: SupplierFormValues,
  savedValues: SupplierFormValues,
): Partial<Contact> => (values[field] !== savedValues[field] ? { [field]: values[field] || null } : {})

interface GetContactTypePayloadOptions {
  additionalContactPersons: ContactPerson[]
  contactPersons: ContactPerson[]
  hasAdditionalContactPersonsChanged: boolean
  savedValues: SupplierFormValues
  values: SupplierFormValues
}

const getCompanyPayload = ({
  additionalContactPersons,
  contactPersons,
  hasAdditionalContactPersonsChanged,
  savedValues,
  values,
}: GetContactTypePayloadOptions): Partial<Contact> => ({
  ...(values.name !== savedValues.name ? { name: values.name } : {}),
  ...(hasAdditionalContactPersonsChanged
    ? {
        contactPersons: [
          ...contactPersons.filter((contactPerson) => contactPerson.isPrimary),
          ...additionalContactPersons,
        ],
      }
    : {}),
})

const getPersonPayload = ({
  additionalContactPersons,
  contactPersons,
  hasAdditionalContactPersonsChanged,
  savedValues,
  values,
}: GetContactTypePayloadOptions): Partial<Contact> => {
  const hasNameChanged = values.firstName !== savedValues.firstName || values.lastName !== savedValues.lastName
  const hasEmailChanged = values.email !== savedValues.email

  return {
    ...(hasNameChanged ? { name: joinPersonName(values.firstName, values.lastName) } : {}),
    ...(hasEmailChanged ? { email: values.email } : {}),
    ...(hasNameChanged || hasEmailChanged || hasAdditionalContactPersonsChanged
      ? { contactPersons: [getPrimaryContactPersonPayload(values, contactPersons), ...additionalContactPersons] }
      : {}),
  }
}

// Builds the PATCH payload for the saved supplier, read for the tab currently on screen.
// Unchanged fields are left out for a reason beyond a lean request: the drawer's Save button is
// disabled while this payload is empty, and the type is only added once something else has
// changed too — so glancing at the other tab without editing anything neither enables Save nor
// flips the supplier's type on submit.
export const getUpdateSupplierContactPayload = (
  values: SupplierFormValues,
  contact: Contact | null,
): Partial<Contact> => {
  if (!contact) {
    return {}
  }

  const savedValues = getSupplierFormDefaultValues(contact, values.type)
  const contactPersons = contact.contactPersons ?? []
  const additionalContactPersons = getAdditionalContactPersonsPayload(values.additionalContactPersons, contactPersons)
  const hasAdditionalContactPersonsChanged = !isEqual(
    additionalContactPersons,
    getAdditionalContactPersonsPayload(savedValues.additionalContactPersons, contactPersons),
  )
  const contactTypePayloadOptions: GetContactTypePayloadOptions = {
    additionalContactPersons,
    contactPersons,
    hasAdditionalContactPersonsChanged,
    savedValues,
    values,
  }

  const payload: Partial<Contact> = {
    ...getTextFieldsPayload(values, savedValues),
    ...getClearableIdFieldPayload('defaultExpenseAccountId', values, savedValues),
    ...getClearableIdFieldPayload('defaultTaxRateId', values, savedValues),
    ...(values.type === ContactType.Person
      ? getPersonPayload(contactTypePayloadOptions)
      : getCompanyPayload(contactTypePayloadOptions)),
  }

  // A Danish expense account does not apply once the supplier leaves Denmark, so the save clears it.
  const leftDenmark =
    contact.countryId === 'DK' && values.countryId !== 'DK' && Boolean(contact.defaultExpenseAccountId)
  const payloadWithAccount = leftDenmark ? { ...payload, defaultExpenseAccountId: null } : payload

  return {
    ...payloadWithAccount,
    ...(Object.keys(payloadWithAccount).length > 0 && values.type !== getSupplierContactType(contact)
      ? { type: values.type }
      : {}),
  }
}
